import React, { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './ControlledRichTextEditor.css';

const ControlledRichTextEditor = ({ value = '', onChange, placeholder = 'Enter text...' }) => {
    const editorRef = useRef(null);
    const quillRef = useRef(null);
    const isUpdatingRef = useRef(false);

    useEffect(() => {
        if (!editorRef.current) return;

        const toolbarOptions = [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'script': 'sub'}, { 'script': 'super' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],
            ['link', 'image', 'video']
        ];

        const quill = new Quill(editorRef.current, {
            modules: {
                toolbar: toolbarOptions
            },
            theme: 'snow',
            placeholder: placeholder
        });
        
        quillRef.current = quill;

        // Set initial value
        if (value) {
            quill.root.innerHTML = value;
        }

        // Handle text changes
        const handleTextChange = () => {
            if (isUpdatingRef.current) return; // Don't trigger onChange during programmatic updates
            
            const html = quill.root.innerHTML;
            // Normalize empty content
            const normalizedHtml = html === '<p><br></p>' ? '' : html;
            onChange(normalizedHtml);
        };

        quill.on('text-change', handleTextChange);

        return () => {
            quill.off('text-change', handleTextChange);
            quillRef.current = null;
        };
    }, []); // Only run once on mount

    // Update editor content when value prop changes (from outside)
    useEffect(() => {
        if (quillRef.current && value !== undefined) {
            const currentContent = quillRef.current.root.innerHTML;
            const normalizedCurrent = currentContent === '<p><br></p>' ? '' : currentContent;
            const normalizedValue = value === '<p><br></p>' ? '' : (value || '');
            
            // Only update if different to avoid cursor jumping
            if (normalizedCurrent !== normalizedValue) {
                isUpdatingRef.current = true;
                const selection = quillRef.current.getSelection();
                const isFocused = document.activeElement === quillRef.current.root;
                quillRef.current.root.innerHTML = normalizedValue || '<p><br></p>';
                // Restore cursor position if editor was focused
                if (selection && isFocused) {
                    setTimeout(() => {
                        if (quillRef.current) {
                            quillRef.current.setSelection(selection);
                        }
                        isUpdatingRef.current = false;
                    }, 0);
                } else {
                    isUpdatingRef.current = false;
                }
            }
        }
    }, [value]);

    return (
        <div className="controlled-rich-text-editor">
            <div ref={editorRef} className="controlled-editor-content" />
        </div>
    );
};

export default ControlledRichTextEditor;

